import type { ShopperSearch } from '@/scapi';
import type { ApproachingPromotion, EditorialPromotion } from '@/types/scapi';

export type ProductPromotionRef = {
    promotionId?: string;
};

export type ProductWithPromotionRefs = {
    productPromotions?: ProductPromotionRef[];
    variants?: Array<{ productId?: string; productPromotions?: ProductPromotionRef[] }>;
};

/** Collects promotion IDs from a product or search-hit variant. */
export function getPromotionIdsFromProduct(product: ProductWithPromotionRefs | undefined | null): Set<string> {
    const ids = new Set<string>();
    if (!product) {
        return ids;
    }

    for (const promo of product.productPromotions ?? []) {
        if (promo.promotionId) {
            ids.add(promo.promotionId);
        }
    }

    return ids;
}

/** Merges promotion IDs from multiple products (e.g. PDP master + selected variant). */
export function getPromotionIdsFromProducts(products: Array<ProductWithPromotionRefs | undefined | null>): Set<string> {
    const ids = new Set<string>();
    for (const product of products) {
        for (const id of getPromotionIdsFromProduct(product)) {
            ids.add(id);
        }
    }
    return ids;
}

/**
 * Resolves promotion IDs for a PLP search hit, including the variant that was added to cart when applicable.
 */
export function getPromotionIdsForSearchHit(
    product: ShopperSearch.schemas['ProductSearchHit'],
    addedProductId?: string
): Set<string> {
    if (addedProductId && product.variants?.length) {
        const variant = product.variants.find((v) => v.productId === addedProductId);
        if (variant) {
            return getPromotionIdsFromProduct(variant as ProductWithPromotionRefs);
        }
    }

    return getPromotionIdsFromProduct(product);
}

/**
 * Keeps only approaching promotions that at least one of the given products contributes to.
 */
export function filterApproachingPromotionsForProducts(
    approachingPromotions: ApproachingPromotion[] | undefined,
    products: Array<ProductWithPromotionRefs | undefined | null>
): ApproachingPromotion[] {
    if (!approachingPromotions?.length || !products.length) {
        return [];
    }

    const eligibleIds = getPromotionIdsFromProducts(products);
    if (eligibleIds.size === 0) {
        return [];
    }

    return approachingPromotions.filter((promotion) => eligibleIds.has(promotion.id));
}

/**
 * Filters basket approaching promotions for a single PLP search hit (and optional added variant SKU).
 */
export function filterApproachingPromotionsForSearchHit(
    approachingPromotions: ApproachingPromotion[] | undefined,
    product: ShopperSearch.schemas['ProductSearchHit'],
    addedProductId?: string
): ApproachingPromotion[] {
    if (!approachingPromotions?.length) {
        return [];
    }

    const eligibleIds = getPromotionIdsForSearchHit(product, addedProductId);
    if (eligibleIds.size === 0) {
        return [];
    }

    return approachingPromotions.filter((promotion) => eligibleIds.has(promotion.id));
}

/** Keeps only editorial promotions that at least one of the given products contributes to. */
export function filterEditorialPromotionsForProducts(
    editorialPromotions: EditorialPromotion[] | undefined,
    products: Array<ProductWithPromotionRefs | undefined | null>
): EditorialPromotion[] {
    if (!editorialPromotions?.length || !products.length) {
        return [];
    }

    const eligibleIds = getPromotionIdsFromProducts(products);
    if (eligibleIds.size === 0) {
        return [];
    }

    return editorialPromotions.filter((promotion) => eligibleIds.has(promotion.id));
}

/** Filters basket editorial promotions for a single PLP search hit (and optional added variant SKU). */
export function filterEditorialPromotionsForSearchHit(
    editorialPromotions: EditorialPromotion[] | undefined,
    product: ShopperSearch.schemas['ProductSearchHit'],
    addedProductId?: string
): EditorialPromotion[] {
    if (!editorialPromotions?.length) {
        return [];
    }

    const eligibleIds = getPromotionIdsForSearchHit(product, addedProductId);
    if (eligibleIds.size === 0) {
        return [];
    }

    return editorialPromotions.filter((promotion) => eligibleIds.has(promotion.id));
}
